import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import type { MentorshipMessage } from '@/types/mentorship';

export function useMentorshipMessages(connectionId: string | null) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [messages, setMessages] = useState<MentorshipMessage[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch messages for a connection
  const fetchMessages = useCallback(async () => {
    if (!connectionId) return;

    setLoading(true);
    const { data, error } = await supabase
      .from('mentorship_messages')
      .select('*')
      .eq('connection_id', connectionId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching messages:', error);
    } else {
      setMessages((data || []) as MentorshipMessage[]);
    }
    setLoading(false);
  }, [connectionId]);

  // Subscribe to realtime messages
  useEffect(() => {
    if (!connectionId) return;

    fetchMessages();

    const channel = supabase
      .channel(`messages-${connectionId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'mentorship_messages',
          filter: `connection_id=eq.${connectionId}`,
        },
        (payload) => {
          const newMessage = payload.new as MentorshipMessage;
          setMessages(prev => [...prev, newMessage]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [connectionId, fetchMessages]);

  // Send a message
  const sendMessage = async (content: string) => {
    if (!user || !connectionId || !content.trim()) return false;

    const { error } = await supabase
      .from('mentorship_messages')
      .insert({
        connection_id: connectionId,
        sender_id: user.id,
        content: content.trim(),
      });

    if (error) {
      toast({
        title: 'Error sending message',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }

    return true;
  };

  // Mark messages as read
  const markAsRead = async () => {
    if (!user || !connectionId) return;

    await supabase
      .from('mentorship_messages')
      .update({ read_at: new Date().toISOString() })
      .eq('connection_id', connectionId)
      .neq('sender_id', user.id)
      .is('read_at', null);
  };

  return {
    messages,
    loading,
    sendMessage,
    markAsRead,
    refetch: fetchMessages,
  };
}
